"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRefundFlow = useRefundFlow;
const react_1 = require("react");
const ApiOrderRepo_1 = require("../../../modules/orders/infra/ApiOrderRepo");
const refundHelpers_1 = require("../refundHelpers");
const refundValidations_1 = require("../refundValidations");
const refundItemUpdaters_1 = require("../refundItemUpdaters");
const errorMessage_1 = require("../errorMessage");
const defaultOrderRepo = (0, ApiOrderRepo_1.createApiOrderRepo)();
function useRefundFlow({ orderDetail, userName, t, onAfterRefund, orderRepo = defaultOrderRepo, }) {
    const [isReturnMode, setIsReturnMode] = (0, react_1.useState)(false);
    const [editableItems, setEditableItems] = (0, react_1.useState)([]);
    const [validationErrors, setValidationErrors] = (0, react_1.useState)(null);
    const [refundType, setRefundType] = (0, react_1.useState)('quantity');
    const [isConfirmModalOpen, setIsConfirmModalOpen] = (0, react_1.useState)(false);
    const [updateMachineStock, setUpdateMachineStock] = (0, react_1.useState)(false);
    const [refundReason, setRefundReason] = (0, react_1.useState)('');
    const [isSubmittingRefund, setIsSubmittingRefund] = (0, react_1.useState)(false);
    const [refundSubmitError, setRefundSubmitError] = (0, react_1.useState)(null);
    const refundTotal = (0, react_1.useMemo)(() => {
        if (!isReturnMode || editableItems.length === 0)
            return 0;
        return (0, refundHelpers_1.calculateRefundTotal)(editableItems);
    }, [editableItems, isReturnMode]);
    (0, react_1.useEffect)(() => {
        if (!isReturnMode || !orderDetail || editableItems.length === 0) {
            setValidationErrors(null);
            return;
        }
        const errors = (0, refundValidations_1.validateAllRefundItems)(editableItems, orderDetail, refundType);
        setValidationErrors(errors);
    }, [editableItems, orderDetail, isReturnMode, refundType]);
    const startReturn = (0, react_1.useCallback)(() => {
        if (!orderDetail)
            return;
        setRefundType('quantity');
        const refundable = (0, refundHelpers_1.computeRefundableItems)(orderDetail.orderItems);
        setEditableItems(refundable.map((i) => (0, refundHelpers_1.initializeRefundItem)(i)));
        setIsReturnMode(true);
    }, [orderDetail]);
    const cancelReturn = (0, react_1.useCallback)(() => {
        setEditableItems([]);
        setIsReturnMode(false);
        setRefundType('quantity');
    }, []);
    const handleRefundTypeChange = (0, react_1.useCallback)((newType) => {
        if (!orderDetail)
            return;
        setRefundType(newType);
        const refundable = (0, refundHelpers_1.computeRefundableItems)(orderDetail.orderItems);
        const initializer = newType === 'price' ? refundHelpers_1.initializeRefundItemByPrice : refundHelpers_1.initializeRefundItem;
        setEditableItems(refundable.map((i) => initializer(i)));
    }, [orderDetail]);
    const openRefundConfirmation = (0, react_1.useCallback)(() => {
        if (!orderDetail)
            return;
        if (validationErrors && !validationErrors.isValid) {
            alert(t('validationError') ||
                'Por favor corrija los errores de validación antes de enviar');
            return;
        }
        if (!(0, refundItemUpdaters_1.hasAnyRefundItems)(editableItems)) {
            alert(t('noItemsToRefund') || 'No hay artículos a devolver');
            return;
        }
        setRefundSubmitError(null);
        setIsConfirmModalOpen(true);
    }, [editableItems, orderDetail, t, validationErrors]);
    const handleHideConfirmModal = (0, react_1.useCallback)(() => {
        if (isSubmittingRefund)
            return;
        setIsConfirmModalOpen(false);
    }, [isSubmittingRefund]);
    const handleQuantityChange = (0, react_1.useCallback)((index, newVal) => {
        setEditableItems((prev) => (0, refundItemUpdaters_1.updateRefundQuantity)(prev, index, newVal));
    }, []);
    const handlePriceChange = (0, react_1.useCallback)((index, newPrice) => {
        setEditableItems((prev) => (0, refundItemUpdaters_1.updateRefundPrice)(prev, index, newPrice));
    }, []);
    const submitRefund = (0, react_1.useCallback)(async () => {
        if (!orderDetail)
            return;
        const refundItems = (0, refundHelpers_1.buildRefundItemsRequest)(editableItems, refundType);
        if (refundItems.length === 0) {
            setRefundSubmitError(t('noItemsToRefund') || 'No hay artículos a devolver');
            return;
        }
        const refund = {
            reason: refundReason,
            synchronizeStock: updateMachineStock,
            orderItems: refundItems,
        };
        setIsSubmittingRefund(true);
        setRefundSubmitError(null);
        try {
            const updatedOrderDetail = await orderRepo.createRefund(orderDetail.id, refund);
            if (!updatedOrderDetail || !updatedOrderDetail.orderItems) {
                throw new Error('Invalid response from server');
            }
            setIsConfirmModalOpen(false);
            setIsReturnMode(false);
            setEditableItems([]);
            setRefundReason('');
            setUpdateMachineStock(false);
            onAfterRefund(updatedOrderDetail);
        }
        catch (err) {
            console.error('Error al devolver artículos', err);
            const message = (0, errorMessage_1.getErrorMessage)(err, t('refundError') || 'Error al procesar la devolución');
            setRefundSubmitError(message);
        }
        finally {
            setIsSubmittingRefund(false);
        }
    }, [
        editableItems,
        onAfterRefund,
        orderDetail,
        orderRepo,
        refundReason,
        refundType,
        t,
        updateMachineStock,
    ]);
    return {
        isReturnMode,
        editableItems,
        validationErrors,
        refundType,
        refundTotal,
        isConfirmModalOpen,
        updateMachineStock,
        refundReason,
        isSubmittingRefund,
        refundSubmitError,
        setUpdateMachineStock,
        setRefundReason,
        startReturn,
        cancelReturn,
        openRefundConfirmation,
        handleHideConfirmModal,
        submitRefund,
        handleQuantityChange,
        handlePriceChange,
        handleRefundTypeChange,
    };
}
